/**
 * Primary hook for ViewJob to access all Ghostwriter-related data.
 *
 * Encapsulates:
 * - Proctor gating (rn_ghostwriter_test must be active)
 * - Eligibility lookup (from the shared autoAppliableJobKeys set)
 * - Application data fetch (always re-fetches on every render)
 * - Consent status resolution (via useResolveConsentStatus)
 * - Button state computation for ViewJob CTA rendering
 *
 * ViewJob-first scenario: if the eligibility set hasn't been hydrated
 * by Homepage yet, falls back to a single-job eligibility + application
 * fetch.
 *
 * Phase B variant: when the user is in the `phase_b` bucket, the proctor
 * payload's `jobKeys` array is the source of truth for eligibility, so the
 * lifecycle pre-hydrates `autoAppliableJobKeys` from it and this hook
 * skips the per-job eligibility backend call entirely. For phase_b,
 * `computeButtonAction` ignores `consentLoading` so the CTA shows
 * "Try auto apply" (or "Auto apply" when consent is cached) immediately
 * while the shared consent fetch runs in the background.
 *
 * Phase E variant (v2b — sequential-conditional): the `phase_e` payload is a
 * traffic-cap allowlist, not the eligibility source of truth. For an
 * allowlisted job this hook runs an eligibility-only `jobEligibility` read on
 * ViewJob and gates the CTA on the result; a non-allowlisted job issues no read
 * and shows no CTA. The CTA stays hidden while the read is in flight (delayed
 * render). Consent does not block the phase_e CTA (see computeButtonAction),
 * matching phase_b so the CTA-latency comparison isn't confounded by consent.
 *
 * Two-step by design (do not collapse): the eligibility read only adds the job
 * key to the auto-appliable set — it does NOT fetch the application. Adding the
 * key flips this effect's `hydrated`/`isAutoAppliable` deps, so on re-entry the
 * `hydrated && isAutoAppliable` branch fetches the application for the button's
 * text state. Net: an ineligible job never fetches an application, and an
 * eligible one fetches it exactly once via that branch — fetching it in the
 * phase_e branch too would double-fetch. The application fetch is off the CTA
 * critical path (the CTA already shows on eligibility), so it doesn't delay
 * render.
 *
 * Consent optimization: the consent check is gated on eligibility, so
 * ineligible jobs never trigger the consent network call.
 */

import { loadingAsyncState, successAsyncState, errorAsyncState } from "./AsyncState";
import { useGhostwriterApi } from "./ghostwriterApi";
import {
  useIsJobAutoAppliable,
  useIsJobInPhaseEAllowlist,
  useGhostwriterJobByKey,
} from "./GhostwriterContext";
import {
  addJobToStore,
  addJobKeyToAutoAppliableSet,
  setAutoAppliableJobKeys,
  setJobsState,
  getSnapshot,
} from "./GhostwriterStore";
import { useGhostwriterGate } from "./useGhostwriterGate";
import { useResolveConsentStatus } from "./useResolveConsentStatus";
import { useMinDurationLoading } from "../hooks/useMinDurationLoading";
import {
  type ScheduleApplyJob,
  type ScheduleApplyStatus,
  ScheduleApplyStatuses,
  ScheduleApplyStatusSeverity,
} from "../models/ScheduleApplyJob";

import { useCallback, useEffect, useMemo, useRef } from "react";

export type GhostwriterButtonAction =
  | "none"
  | "loading"
  | "try-auto-apply"
  | "auto-apply"
  | "in-progress"
  | "ready-for-review"
  | "applied"
  | "error";

export const GHOSTWRITER_PRIMARY_ACTIONS: GhostwriterButtonAction[] = [
  "try-auto-apply",
  "auto-apply",
  "in-progress",
  "ready-for-review",
  "applied",
];

export function shouldShowSecondaryApply(buttonAction: GhostwriterButtonAction): boolean {
  return buttonAction !== "applied" && GHOSTWRITER_PRIMARY_ACTIONS.includes(buttonAction);
}

const STATUS_ACTION_MAP = new Map<ScheduleApplyStatus, GhostwriterButtonAction>([
  [ScheduleApplyStatuses.NotStarted, "auto-apply"],
  [ScheduleApplyStatuses.AutoApplyCancelled, "auto-apply"],
  [ScheduleApplyStatuses.AnalyzingJob, "in-progress"],
  [ScheduleApplyStatuses.FillingOutApplication, "in-progress"],
  [ScheduleApplyStatuses.SubmittingApplication, "in-progress"],
  [ScheduleApplyStatuses.RetryPendingCaptcha, "in-progress"],
  [ScheduleApplyStatuses.ReadyToReview, "ready-for-review"],
  [ScheduleApplyStatuses.MissingRequiredInfo, "ready-for-review"],
  [ScheduleApplyStatuses.Applied, "applied"],
  [ScheduleApplyStatuses.AutoApplyFailed, "error"],
  [ScheduleApplyStatuses.SubmissionFailed, "error"],
]);

function computeButtonAction(
  isEnabled: boolean | null,
  isAutoAppliable: boolean,
  hasConsented: boolean,
  consentLoading: boolean,
  ghostwriterJob: ScheduleApplyJob | null,
  appliableLoading: boolean,
  consentExempt: boolean,
): GhostwriterButtonAction {
  if (isEnabled !== true) return "none";
  if (appliableLoading) return "loading";
  if (!isAutoAppliable) return "none";
  // Consent-exempt buckets (phase_b, phase_e) show Try/Auto apply immediately
  // while consent resolves in the background, so the CTA is gated on eligibility
  // alone. For phase_e this keeps the CTA-latency comparison against phase_b
  // from being confounded by consent.
  if (!consentExempt && consentLoading) return "loading";
  if (!hasConsented) return "try-auto-apply";
  if (!ghostwriterJob) return "auto-apply";

  const { applicationStatus } = ghostwriterJob;
  return (
    STATUS_ACTION_MAP.get(applicationStatus) ??
    (applicationStatus.severity === ScheduleApplyStatusSeverity.Error ? "error" : "in-progress")
  );
}

export interface GhostwriterViewJobResult {
  buttonAction: GhostwriterButtonAction;
  applicationId: string | null;
  /**
   * True if the user is in the phase A (synchronous flow) bucket.
   * Consumers branch CTA copy on this flag — see RN-3662.
   */
  isPhaseA: boolean;
  /**
   * True if the user is in the phase B bucket (payload fast-path). phase_b
   * now shares phase_c's synchronous Prefill UI flow on ViewJob. See RN-5091.
   */
  isPhaseB: boolean;
  /**
   * True if the user is in the phase C bucket — phase A's Prefill flow with
   * the standard 'Auto apply' / 'Try auto apply' CTA copy. ViewJob opens the
   * prefilling modal for phase A, phase B, or phase C. See RN-5091.
   */
  isPhaseC: boolean;
  isAutoApplyLoading: boolean;
  beginAutoApplyLoading: () => void;
  /**
   * True when the user should get phase A's "Prefill" ViewJob experience
   * (CTA copy, prefilling modal entry). Phase D is a phase-A clone that
   * also adds the in-review intervention screen, so on ViewJob it must
   * look identical to phase A. Prefer this over `isPhaseA` for any
   * Prefill-experience branch. See RN-3367.
   */
  usesPrefillExperience: boolean;
  startAutoApply: () => Promise<void>;
  stopAutoApply: () => void;
  /**
   * Re-fetches the ghostwriter application for this job and writes it
   * into the shared store. Used when the prefilling modal dismisses so
   * ViewJob can show the latest CTA state.
   */
  refreshApplication: () => Promise<void>;
}

export function useGhostwriterForViewJob(jobKey: string | null): GhostwriterViewJobResult {
  const { isEnabled, isPhaseA, isPhaseB, isPhaseC, isPhaseD, isPhaseE } = useGhostwriterGate();
  const {
    fetchAutoAppliableJobKeys,
    fetchApplicationForJob,
    fetchEligibilityAndApplication,
    startScheduleApply,
  } = useGhostwriterApi();
  const { isAutoAppliable, loading: appliableLoading, hydrated } = useIsJobAutoAppliable(jobKey);
  // phase_e: the allowlist caps which jobs run the real-time read.
  const isInPhaseEAllowlist = useIsJobInPhaseEAllowlist(jobKey);
  const { job: ghostwriterJob } = useGhostwriterJobByKey(jobKey);
  // Ineligible jobs never trigger the consent network call.
  const shouldResolveConsent = isEnabled === true && isAutoAppliable;
  const { hasConsented, consentLoading } = useResolveConsentStatus(shouldResolveConsent);

  useEffect(() => {
    if (!isEnabled || !jobKey) return;

    if (hydrated && isAutoAppliable) {
      // Also the phase_e text-state fetch: once the eligibility read flips
      // `isAutoAppliable`, this branch fetches the application on re-entry
      // (see the "Two-step by design" note in the hook doc above).
      setJobsState(loadingAsyncState(getSnapshot().jobs.data));
      fetchApplicationForJob(jobKey)
        .then((job) => {
          if (job) {
            addJobToStore(job);
          } else {
            setJobsState(successAsyncState(getSnapshot().jobs.data ?? []));
          }
        })
        .catch((err: Error) => setJobsState(errorAsyncState(err, getSnapshot().jobs.data)));
    } else if (isPhaseE === true && isInPhaseEAllowlist && !appliableLoading) {
      // Phase E (v2b): eligibility-only read gates the CTA. On eligible,
      // adding the key flips `hydrated && isAutoAppliable` so the branch
      // above fetches the application on re-entry — do NOT fetch it here or
      // it double-fetches. Non-allowlisted phase_e jobs never reach here,
      // and are excluded from the combined read below.
      // Eligible results are sticky: the key persists in the in-memory set, so
      // revisits skip the read until the store resets (app restart, logout,
      // switch). Ineligible jobs aren't cached, so they re-check on each revisit.
      fetchAutoAppliableJobKeys([jobKey])
        .then((keys) => {
          if (keys.includes(jobKey)) {
            addJobKeyToAutoAppliableSet(jobKey);
          }
        })
        // Route to the eligibility slice (this read populates it), matching
        // useGhostwriterForHomepage's use of the same fetchAutoAppliableJobKeys.
        .catch((err: Error) =>
          setAutoAppliableJobKeys(errorAsyncState(err, getSnapshot().autoAppliableJobKeys.data)),
        );
    } else if (!appliableLoading && !isPhaseB && isPhaseE !== true) {
      // Phase B trusts its payload; phase_e uses the eligibility-only read
      // above. All other enabled buckets fall back to the combined read.
      setJobsState(loadingAsyncState(getSnapshot().jobs.data));
      fetchEligibilityAndApplication(jobKey)
        .then((result) => {
          if (result.eligible) {
            addJobKeyToAutoAppliableSet(jobKey);
            if (result.application) {
              addJobToStore(result.application);
            } else {
              setJobsState(successAsyncState(getSnapshot().jobs.data ?? []));
            }
          } else {
            setJobsState(successAsyncState(getSnapshot().jobs.data ?? []));
          }
        })
        .catch((err: Error) => setJobsState(errorAsyncState(err, getSnapshot().jobs.data)));
    }
  }, [
    isEnabled,
    isPhaseB,
    isPhaseE,
    isInPhaseEAllowlist,
    jobKey,
    hydrated,
    isAutoAppliable,
    appliableLoading,
    fetchAutoAppliableJobKeys,
    fetchApplicationForJob,
    fetchEligibilityAndApplication,
  ]);

  const {
    isLoading: isAutoApplyLoading,
    start: beginAutoApplyLoading,
    stop: stopAutoApply,
  } = useMinDurationLoading();

  const startAutoApply = useCallback(async () => {
    if (!jobKey) throw new Error("Cannot start auto apply without a job key");
    beginAutoApplyLoading();
    try {
      await startScheduleApply(jobKey);
    } catch (err) {
      stopAutoApply();
      throw err;
    }
  }, [jobKey, startScheduleApply, beginAutoApplyLoading, stopAutoApply]);

  const refreshApplication = useCallback(async () => {
    if (!jobKey) return;

    try {
      const job = await fetchApplicationForJob(jobKey);
      if (job) {
        addJobToStore(job);
      }
    } catch (err) {
      console.warn("[useGhostwriterForViewJob] refreshApplication failed", err);
    }
  }, [jobKey, fetchApplicationForJob]);

  const buttonAction = useMemo(() => {
    // phase_b and phase_e don't block the CTA on consent (see computeButtonAction).
    const consentExempt = isPhaseB === true || isPhaseE === true;
    return computeButtonAction(
      isEnabled,
      isAutoAppliable,
      hasConsented,
      consentLoading,
      ghostwriterJob,
      appliableLoading,
      consentExempt,
    );
  }, [
    isEnabled,
    isAutoAppliable,
    hasConsented,
    consentLoading,
    ghostwriterJob,
    appliableLoading,
    isPhaseB,
    isPhaseE,
  ]);

  const isMountedRef = useRef(false);
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    stopAutoApply();
  }, [buttonAction, stopAutoApply]);

  return {
    buttonAction,
    applicationId: ghostwriterJob?.applicationId ?? null,
    isPhaseA: isPhaseA === true,
    isPhaseB: isPhaseB === true,
    isPhaseC: isPhaseC === true,
    isAutoApplyLoading,
    beginAutoApplyLoading,
    usesPrefillExperience: isPhaseA === true || isPhaseD === true,
    startAutoApply,
    stopAutoApply,
    refreshApplication,
  };
}
